// Entrar com código por e-mail, primeiro cadastro da empresa e escolha de
// empresa (para quem participa de mais de uma conta).
import { DIAS_TESTE, LANCAMENTO } from '../config.js';
import { aceitarTermos, criarEmpresa } from '../dados.js';
import { h, icone } from '../lib/dom.js';
import { tirarPedido } from '../lib/depois-de-entrar.js';
import { precoPorMes } from '../lib/preco.js';
import { documentoValido, emailValido, normalizarCelular, normalizarDocumento, tipoDoDocumento, UFS } from '../lib/validacao.js';
import { ir } from '../rotas.js';
import { mensagemDeErro } from '../lib/erros.js';
import { rotaDoLink } from '../lib/motorista.js';
import { nomeProprio } from '../lib/mascaras.js';
import { rotuloPapel } from '../lib/papeis.js';
import { carregarVinculos, escolherEmpresa, prepararUsuario, sair, sessao } from '../sessao.js';
import { supabase } from '../supabase.js';
import { casca } from '../ui/casca.js';
import { confirmarSaida } from '../ui/sinal.js';
import { avisar, campo, faixa, formulario, secao } from '../ui/componentes.js';
import { avisoDosTermos, linksDosTermos } from './legal.js';

// O e-mail do código pedido fica guardado por 1 hora (o tempo de validade do
// código): no iPhone, o app instalado pode reabrir do zero enquanto a pessoa
// foi ler o e-mail, e ela precisa conseguir voltar para digitar o código.
const CHAVE_EMAIL = 'maxcargo-email-login';
const VALIDADE_MS = 60 * 60 * 1000;
let emailEmMemoria = null;

function guardarEmail(email) {
  emailEmMemoria = { email, em: Date.now() };
  try {
    localStorage.setItem(CHAVE_EMAIL, JSON.stringify(emailEmMemoria));
  } catch {
    /* segue só com a memória */
  }
}

function lerPedido() {
  let pedido = emailEmMemoria;
  try {
    pedido = JSON.parse(localStorage.getItem(CHAVE_EMAIL) ?? 'null') ?? pedido;
  } catch {
    /* usa a memória */
  }
  if (!pedido?.email || Date.now() - pedido.em > VALIDADE_MS) return null;
  return pedido;
}

function lerEmail() {
  return lerPedido()?.email ?? null;
}

function esquecerEmail() {
  emailEmMemoria = null;
  try {
    localStorage.removeItem(CHAVE_EMAIL);
  } catch {
    /* nada */
  }
}

// Hora do pedido, para a pessoa achar o e-mail certo na caixa de entrada.
function horaDoPedido(pedido) {
  return new Date(pedido.em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

export function marca() {
  return h('div', { class: 'marca' }, icone('caminhao', { tamanho: 30 }), h('strong', null, 'HSA Frota'));
}

// Para onde o link do e-mail volta: este mesmo endereço do app, sem o "#". O
// Supabase aceita endereços no mesmo servidor do "Site URL" do projeto
// (localhost, enquanto o app roda no computador) ou da lista de liberados.
function enderecoDoApp() {
  return `${location.origin}${location.pathname}`;
}

async function pedirCodigo(email) {
  const { error } = await supabase.auth.signInWithOtp({
    email,
    options: { shouldCreateUser: true, emailRedirectTo: enderecoDoApp() },
  });
  if (error) throw error;
}

// Para onde ir depois de entrar ou de carregar a sessão.
export function destinoInicial() {
  if (!sessao.usuario) return '/entrar';
  // Quem foi convidado e ainda não usa nenhuma conta vê o convite primeiro.
  if (sessao.vinculos.length === 0) {
    // Celular do motorista que perdeu o acesso: não cria conta.
    if (sessao.usuario.is_anonymous) return '/sem-acesso';
    return sessao.convites.length > 0 ? '/convites' : '/empresa/nova';
  }
  if (!sessao.empresa) return '/empresas';
  if (sessao.papel === 'agenciador') return '/a';
  if (sessao.papel === 'motorista') return '/m';
  // A tela que o site pediu antes de entrar (o "Assinar")
  return tirarPedido() ?? '/inicio';
}

// ---------------------------------------------------------------------
export function telaEntrar() {
  const form = formulario({
    filhos: [
      campo({
        rotulo: 'Seu e-mail',
        nome: 'email',
        tipo: 'email',
        obrigatorio: true,
        valor: lerEmail() ?? '',
        atributos: { autocomplete: 'email', inputmode: 'email', autocapitalize: 'none', spellcheck: 'false', placeholder: 'nome@gmail.com' },
      }),
    ],
    textoBotao: 'Receber código',
    textoEnviando: 'Enviando código…',
    comDetalhe: true,
    validar: (d) => (emailValido(d.email) ? {} : { email: 'Digite um e-mail válido, como nome@gmail.com.' }),
    aoEnviar: async ({ email }) => {
      const limpo = email.trim().toLowerCase();
      await pedirCodigo(limpo);
      guardarEmail(limpo);
      ir('/codigo');
    },
  });
  const pendente = lerPedido();
  return h('div', { class: 'entrada' },
    marca(),
    h('div', null,
      h('h1', null, 'Entrar'),
      h('p', { class: 'subtitulo' }, 'Digite seu e-mail. Vamos mandar um código para você entrar, sem senha.')),
    form,
    pendente &&
      h('a', { class: 'botao texto', href: '#/codigo' }, `Já tenho o código enviado para ${pendente.email} às ${horaDoPedido(pendente)}`),
    souMotorista(),
    linksDosTermos());
}

// O motorista entra pelo link do WhatsApp. No app posto na Tela de Início do
// iPhone (e em quem abriu o app antes de tocar no link), ele cola o link aqui.
function souMotorista() {
  const erro = h('p', { class: 'erro-campo', role: 'alert' });
  const entrada = h('input', {
    id: 'link-do-motorista', type: 'url', inputmode: 'url', autocomplete: 'off', autocapitalize: 'none', spellcheck: 'false',
    placeholder: 'Cole aqui o link do WhatsApp', 'aria-describedby': 'link-do-motorista-erro',
  });
  erro.id = 'link-do-motorista-erro';
  const usar = (texto) => {
    const rota = rotaDoLink(texto);
    if (!rota) {
      erro.textContent = 'Isto não é o link do app. No WhatsApp, toque e segure no link, escolha Copiar e tente de novo.';
      return;
    }
    erro.textContent = '';
    ir(rota);
  };
  return h('details', { class: 'sou-motorista' },
    h('summary', null, 'Sou motorista ou agenciador'),
    h('p', null, 'Entre com o link que o dono da frota mandou para você no WhatsApp.'),
    h('div', { class: 'campo' }, h('label', { for: 'link-do-motorista' }, 'Link do WhatsApp'), entrada, erro),
    h('div', { class: 'acoes' },
      h('button', {
        class: 'botao cheio',
        type: 'button',
        onClick: async () => {
          try {
            const texto = await navigator.clipboard.readText();
            entrada.value = texto;
            usar(texto);
          } catch {
            erro.textContent = 'Não deu para colar sozinho: toque e segure no campo acima, escolha Colar e toque em "Entrar com o link".';
          }
        },
      }, 'Colar o link'),
      h('button', { class: 'botao secundario cheio', type: 'button', onClick: () => usar(entrada.value) }, 'Entrar com o link')));
}

// ---------------------------------------------------------------------
export function telaCodigo() {
  const pedido = lerPedido();
  if (!pedido) {
    ir('/entrar', { substituir: true });
    return null;
  }
  const { email } = pedido;
  const hora = h('strong', null, horaDoPedido(pedido));

  // O código do Supabase tem 6 números por padrão e pode ser configurado de 6
  // a 10. O app aceita qualquer um desses tamanhos.
  const form = formulario({
    filhos: [
      campo({
        rotulo: 'Código do e-mail',
        nome: 'codigo',
        obrigatorio: true,
        dica: 'Digite todos os números que vieram no e-mail mais recente.',
        atributos: {
          inputmode: 'numeric',
          autocomplete: 'one-time-code',
          maxlength: '10',
          pattern: '[0-9]*',
          class: 'codigo-entrada',
        },
        aoMudar: (e) => {
          e.target.value = e.target.value.replace(/\D/g, '').slice(0, 10);
        },
      }),
    ],
    textoBotao: 'Entrar',
    textoEnviando: 'Conferindo…',
    comDetalhe: true,
    validar: (d) => (/^\d{6,10}$/.test(d.codigo) ? {} : { codigo: 'Digite todos os números do código (6 a 10 números).' }),
    aoEnviar: async ({ codigo }) => {
      const { data, error } = await supabase.auth.verifyOtp({ email, token: codigo, type: 'email' });
      if (error) {
        // Código errado: limpa o campo para digitar de novo
        const entrada = form.querySelector('[name="codigo"]');
        entrada.value = '';
        entrada.focus();
        throw error;
      }
      await prepararUsuario(data.user);
      esquecerEmail();
      ir(destinoInicial(), { substituir: true });
    },
  });

  // Reenviar só depois de 60 segundos (limite do Supabase)
  const reenviar = h('button', { class: 'botao secundario cheio', type: 'button' });
  let restante = 0;
  let relogio = null;
  const mostrar = () => {
    reenviar.textContent = restante > 0 ? `Pedir outro código (${restante}s)` : 'Pedir outro código';
    reenviar.disabled = restante > 0;
  };
  const contar = () => {
    restante = 60;
    mostrar();
    clearInterval(relogio);
    relogio = setInterval(() => {
      if (!reenviar.isConnected) return clearInterval(relogio);
      restante -= 1;
      mostrar();
      if (restante <= 0) clearInterval(relogio);
    }, 1000);
  };
  contar();
  reenviar.addEventListener('click', async () => {
    reenviar.disabled = true;
    try {
      await pedirCodigo(email);
      guardarEmail(email);
      const novo = horaDoPedido(emailEmMemoria);
      hora.textContent = novo;
      avisar(`E-mail novo enviado às ${novo}. O anterior não vale mais.`);
      contar();
    } catch (erro) {
      mostrar();
      avisar(mensagemDeErro(erro), 'erro');
    }
  });

  // O e-mail padrão do Supabase traz um link (e, conforme o modelo, também o
  // código). O link volta para este app já com a pessoa dentro. Aberto em
  // outro aparelho, ele faz entrar lá e gasta o código daqui.
  return h('div', { class: 'entrada' },
    marca(),
    h('div', null,
      h('h1', null, 'Confira seu e-mail'),
      h('p', { class: 'subtitulo' }, 'Mandamos um e-mail para ', h('strong', null, email), ' às ', hora, '.')),
    h('ul', { class: 'dicas' },
      h('li', null, 'Abra o e-mail ', h('strong', null, 'neste aparelho'), ' e toque no link, ou digite o código aqui.'),
      h('li', null, 'Vale só o e-mail ', h('strong', null, 'mais novo'), '. O código nunca vem por SMS.'),
      h('li', null, 'Pode levar alguns minutos. Olhe também no Spam.')),
    form,
    h('div', { class: 'acoes' },
      reenviar,
      h('a', { class: 'botao texto', href: '#/entrar' }, 'Usar outro e-mail')));
}

// ---------------------------------------------------------------------
export function telaEmpresaNova() {
  const primeira = sessao.vinculos.length === 0;
  const docCampo = campo({
    rotulo: 'CPF ou CNPJ do titular',
    nome: 'documento',
    obrigatorio: true,
    formato: 'documento',
    dica: 'Aparece nos relatórios e recibos. Cada CPF/CNPJ tem uma conta só.',
    atributos: { autocomplete: 'off', autocapitalize: 'characters', spellcheck: 'false' },
  });

  const form = formulario({
    filhos: [
      docCampo,
      campo({ rotulo: 'Nome completo ou razão social', nome: 'nome', obrigatorio: true, atributos: { autocomplete: 'organization' } }),
      campo({ rotulo: 'Nome fantasia', nome: 'nome_fantasia', dica: 'Opcional. É o nome que aparece no topo do app.' }),
      campo({
        rotulo: 'Celular com DDD',
        nome: 'celular',
        tipo: 'tel',
        obrigatorio: true,
        atributos: { autocomplete: 'tel-national', inputmode: 'tel', placeholder: '(48) 99999-0000' },
      }),
      campo({ rotulo: 'E-mail da empresa', nome: 'email', tipo: 'email', valor: sessao.usuario?.email ?? '', atributos: { autocomplete: 'email', inputmode: 'email', autocapitalize: 'none' } }),
      h('div', { class: 'grade-uf' },
        campo({ rotulo: 'Cidade', nome: 'cidade', formato: 'nome', atributos: { autocomplete: 'address-level2' } }),
        campo({ rotulo: 'UF', nome: 'uf', tipo: 'select', opcoes: [['', '—'], ...UFS.map((u) => [u, u])] })),
    ],
    textoBotao: primeira ? `Começar teste grátis de ${DIAS_TESTE} dias` : 'Criar conta',
    textoEnviando: 'Criando a conta…',
    validar: (d) => {
      const erros = {};
      const tipo = tipoDoDocumento(d.documento);
      if (!tipo) erros.documento = 'Digite o CPF (11 números) ou o CNPJ (14 caracteres).';
      else if (!documentoValido(d.documento, tipo)) erros.documento = tipo === 'PF' ? 'CPF inválido. Confira os números.' : 'CNPJ inválido. Confira o que foi digitado.';
      if (d.nome.length < 2) erros.nome = 'Informe o nome.';
      if (!normalizarCelular(d.celular)) erros.celular = 'Informe DDD + número, como (48) 99999-0000.';
      if (d.email && !emailValido(d.email)) erros.email = 'E-mail inválido.';
      return erros;
    },
    aoEnviar: async (d) => {
      const documento = normalizarDocumento(d.documento);
      let id;
      try {
        const tipo = tipoDoDocumento(d.documento);
        // Pessoa física: o nome segue o padrão dos nomes de pessoa.
        const nome = tipo === 'PF' ? nomeProprio(d.nome) : d.nome;
        // Ao criar a conta, a pessoa aceita os Termos de uso e a Política de
        // privacidade (o aviso fica embaixo do botão); o aceite fica gravado.
        // (Servidor ainda sem a função: segue sem gravar o aceite.)
        await aceitarTermos().catch((erro) => {
          if (erro?.code !== 'PGRST202') throw erro;
        });
        id = await criarEmpresa({ ...d, nome, tipo_pessoa: tipo, documento, celular: normalizarCelular(d.celular) });
      } catch (erro) {
        // A conta pode ter sido criada e a resposta se perdido (sinal ruim):
        // se ela já é desta pessoa, segue em frente.
        if (!/^DOCUMENTO_JA_CADASTRADO/.test(erro?.message ?? '')) throw erro;
        await carregarVinculos();
        const minha = sessao.vinculos.find((v) => v.empresa.documento === documento);
        if (!minha) throw erro;
        id = minha.empresa.id;
      }
      await carregarVinculos();
      escolherEmpresa(sessao.vinculos.find((v) => v.empresa.id === id) ?? null);
      // Veio pelo "Assinar" do site: vai direto para a assinatura.
      ir(tirarPedido() ?? '/inicio?bemvindo=1', { substituir: true });
    },
  });

  const textoDoBotao = primeira ? `Começar teste grátis de ${DIAS_TESTE} dias` : 'Criar conta';
  const conteudo = [
    primeira &&
      faixa(
        `Durante o teste está tudo liberado, sem cobrança e sem cartão. Depois, ${precoPorMes()}. Preço de lançamento para as primeiras empresas: ${precoPorMes({ lancamento: true })}, nos primeiros ${LANCAMENTO.meses} meses.`,
        'info',
        'Bem-vindo ao HSA Frota',
      ),
    form,
    avisoDosTermos(textoDoBotao),
    primeira && sessao.convites.length > 0 &&
      h('a', { class: 'botao texto', href: '#/convites' }, 'Recebi um convite: quero entrar na conta de outra empresa'),
  ];

  if (primeira) {
    return h('div', { class: 'entrada' },
      marca(),
      h('div', null, h('h1', null, 'Cadastre sua empresa'), h('p', { class: 'subtitulo' }, 'Só falta isso para começar.')),
      conteudo,
      h('button', {
        class: 'botao texto',
        type: 'button',
        onClick: async () => {
          if (!(await confirmarSaida())) return;
          await sair();
          ir('/entrar');
        },
      }, 'Sair'));
  }
  return casca({ titulo: 'Nova conta', voltar: '/empresas', abas: false, conteudo });
}

// ---------------------------------------------------------------------
export function telaEmpresas() {
  const lista = sessao.vinculos.map((v) =>
    h('button', {
      class: 'cartao',
      type: 'button',
      onClick: () => {
        escolherEmpresa(v);
        ir(destinoInicial());
      },
    },
    h('div', { class: 'cartao-corpo' },
      h('span', { class: 'cartao-titulo' }, v.empresa.nome_fantasia || v.empresa.nome),
      h('span', { class: 'cartao-linha' }, rotuloPapel(v.papel))),
    icone('seta', { classe: 'icone seta' })));

  return casca({
    titulo: 'Escolha a empresa',
    abas: false,
    conteudo: [
      sessao.convites.length > 0 && h('a', { class: 'botao cheio', href: '#/convites' },
        sessao.convites.length === 1 ? 'Ver o convite que você recebeu' : `Ver os ${sessao.convites.length} convites que você recebeu`),
      secao('Suas contas', h('div', { class: 'lista' }, lista)),
      !sessao.usuario?.is_anonymous && h('a', { class: 'botao secundario cheio', href: '#/empresa/nova' }, 'Cadastrar outra empresa'),
    ],
  });
}

export { rotuloPapel };

